// @spec IAM-STR-016
import { createFileRoute } from '@tanstack/react-router';

import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useInvitation } from '@/features/invitations/api/use-invitation';
import { InvitationExpired } from '@/features/invitations/components/invitation-expired';
import { InvitationLanding } from '@/features/invitations/components/invitation-landing';
import type { InvitationInvalidReason } from '@/features/invitations/types';

export const Route = createFileRoute('/invite/$token')({
  component: InvitationPage,
});

/**
 * Maps API error codes to display reasons for the InvitationExpired component.
 */
function getInvalidReason(
  errorCode: string | null,
  status: number | undefined
): InvitationInvalidReason {
  if (errorCode === 'EXPIRED' || status === 410) {
    return 'expired';
  }
  if (errorCode === 'REVOKED') {
    return 'revoked';
  }
  if (errorCode === 'ALREADY_ACCEPTED') {
    return 'already_accepted';
  }
  // Default for 404 or unknown errors
  return 'not_found';
}

/**
 * Loading skeleton for the invitation page.
 */
function InvitationSkeleton() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4">
            <Skeleton className="h-20 w-20 rounded-full" />
          </div>
          <Skeleton className="mx-auto h-8 w-48" />
          <Skeleton className="mx-auto mt-2 h-4 w-64" />
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-lg border border-border bg-muted/50 p-4 space-y-3">
            <div className="flex justify-between">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-4 w-24" />
            </div>
            <div className="flex justify-between">
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-4 w-20" />
            </div>
            <div className="flex justify-between">
              <Skeleton className="h-4 w-12" />
              <Skeleton className="h-4 w-40" />
            </div>
          </div>
          <Skeleton className="h-10 w-full" />
          <Skeleton className="mx-auto h-3 w-56" />
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Invitation landing page route component.
 *
 * Fetches invitation details by token from the URL and displays:
 * - Loading skeleton while fetching
 * - InvitationExpired for invalid/expired invitations
 * - InvitationLanding for valid pending invitations
 */
function InvitationPage() {
  const { token } = Route.useParams();
  const { data, isLoading, isError, error, errorCode } = useInvitation({ token });

  // Show loading skeleton
  if (isLoading) {
    return <InvitationSkeleton />;
  }

  // Show error state for invalid invitations
  if (isError || !data) {
    const reason = getInvalidReason(errorCode, error?.status);
    return <InvitationExpired reason={reason} />;
  }

  // Show invitation landing page
  return <InvitationLanding invitation={data} />;
}
